import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { PublicSiteFooter } from '../components/layout/PublicSiteFooter';
import { BrandWordmark } from '../components/brand/BrandWordmark';

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

/** Primary questions — reassuring, plain language */
const ESSENTIAL_FAQ = [
  {
    id: 'essential-1',
    q: (
      <span className="inline-flex flex-wrap items-baseline gap-0">
        <span>What is </span>
        <BrandWordmark variant="inline" as="span" />
        <span>?</span>
      </span>
    ),
    a: (
      <>
        <BrandWordmark variant="inline" as="span" /> is a private place to build your family tree, save stories and
        documents, and invite relatives to help—so the people and moments you care about stay together for the
        generations ahead.
      </>
    ),
  },
  {
    id: 'essential-2',
    q: 'Is my family tree private?',
    a: 'Yes. Your tree is private by default. You choose whether to invite others and what they can do. We do not sell your family information, and you stay in control of what you save and share.',
  },
  {
    id: 'essential-3',
    q: 'Can I invite relatives?',
    a: 'Yes. You can invite family to view or help build a tree you share with them. You decide who joins and what they can change—so collaboration stays intentional and calm.',
  },
  {
    id: 'essential-4',
    q: 'Can I record voice stories?',
    a: 'Yes. You can record stories in your own voice or write them out. Attach them to people in your tree so voices and words stay with the family story—not lost in a camera roll or inbox.',
  },
  {
    id: 'essential-5',
    q: 'What kinds of documents can I upload?',
    a: 'You can upload photos, letters, certificates, and other family papers in common formats (such as images and PDFs). Link them to people in your tree so documents sit alongside names and stories—not buried in a drawer.',
  },
  {
    id: 'essential-6',
    q: 'Who can edit the tree?',
    a: 'You decide. People you invite can be viewers (read-only) or editors (can add and change what you allow). Owners control the tree; everyone else has only the access you give them.',
  },
  {
    id: 'essential-7',
    q: 'Can I have more than one family tree?',
    a: 'Yes. You can create more than one tree—for example, separate lines of your family or a tree you help someone else with—each with its own people, stories, and documents.',
  },
  {
    id: 'essential-8',
    q: 'What happens to my archive over time?',
    a: 'Your archive is yours. You can export your work, download backups, and manage your account as your family grows. We’re built to help you preserve—not trap—what you’ve gathered, so your legacy stays portable and in your hands.',
  },
];

/** Shorter follow-ups for people who want more detail */
const MORE_FAQ = [
  {
    title: 'DNA & genetic data',
    items: [
      {
        id: 'more-dna-1',
        q: 'Where does DNA data come from?',
        a: 'You upload raw DNA from a testing service you already used (such as AncestryDNA, 23andMe, MyHeritage, or Family Tree DNA). We do not perform DNA testing here—we help you use what you already have, if you choose to.',
      },
      {
        id: 'more-dna-2',
        q: 'Are DNA insights medical?',
        a: 'No. Insights here are for exploring family history only—not health, diagnosis, or treatment. If you have health questions, please speak with a qualified professional.',
      },
    ],
  },
  {
    title: 'Sharing & discovery',
    items: [
      {
        id: 'more-share-1',
        q: 'What is cross-tree discovery?',
        a: 'When you opt in, the platform can help surface possible connections between your tree and another member’s tree—so you can explore shared ancestry together, with consent on both sides.',
      },
      {
        id: 'more-share-2',
        q: 'What are connection requests?',
        a: 'They are invitations to link your tree with someone else’s in a controlled way. You can accept or decline—nothing connects without your choice.',
      },
    ],
  },
  {
    title: 'Export & peace of mind',
    items: [
      {
        id: 'more-exp-1',
        q: 'Can I export my archive?',
        a: 'Yes. From your tree’s settings you can download exports such as a print-friendly Legacy Book and data backups. Your content is yours to keep.',
      },
      {
        id: 'more-exp-2',
        q: 'Who owns my data?',
        a: 'You do. We help you organize and preserve it. You can remove content or seek full account deletion when you need to.',
      },
    ],
  },
];

const accordionItemClass =
  'border-0 border-b border-[#e8e2d8] bg-transparent last:border-b-0';
const triggerClass =
  'py-5 text-left text-[1.02rem] font-medium leading-snug text-[#1c1917] hover:text-[#2c2824] hover:no-underline [&[data-state=open]>svg]:text-[#8b7355]';
const contentClass = 'pb-6 text-[0.95rem] leading-[1.75] text-[#4a433c]';

const FAQ = () => {
  return (
    <div className="min-h-screen bg-[#faf8f5] text-[#1c1917]">
      <div className="noise-overlay pointer-events-none fixed inset-0 z-[1]" aria-hidden />

      {/* Simple top bar — functional, calm */}
      <header className="sticky top-0 z-40 border-b border-[#e8e2d8]/90 bg-[#faf8f5]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link
            to="/home"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#5c534a] transition hover:text-[#1c1917]"
          >
            <ArrowLeft className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
            Back to home
          </Link>
          <Link
            to="/home"
            className="group flex items-center gap-2 text-[#1c1917] transition hover:opacity-90"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#c9a961]/20 text-[0.85rem] text-[#5c4a32] ring-1 ring-[#c9a961]/30">
              G
            </span>
            <span className="hidden sm:inline">
              <BrandWordmark variant="navFaq" className="text-lg" />
            </span>
          </Link>
        </div>
      </header>

      <main className="relative z-10">
        <section className="border-b border-[#e8e2d8]/80 px-4 pb-14 pt-12 sm:px-6 sm:pb-16 sm:pt-16">
          <div className="mx-auto max-w-2xl text-center">
            <motion.p
              {...fade}
              className="text-[0.7rem] font-medium uppercase tracking-[0.22em] text-[#8b7355]"
            >
              Help &amp; reassurance
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4 font-serif text-3xl font-normal leading-tight tracking-tight text-[#1c1917] sm:text-4xl"
            >
              Frequently asked questions
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-[#5c534a]"
            >
              Straightforward answers—no jargon, no rush. If you’re deciding whether this is right for your family,
              start here.
            </motion.p>
          </div>
        </section>

        {/* Essential questions */}
        <section className="px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-2xl">
            <h2 className="mb-8 font-serif text-xl font-normal text-[#1c1917] sm:text-2xl">
              Questions you may have
            </h2>
            <Accordion type="single" collapsible className="divide-y divide-[#e8e2d8] rounded-2xl border border-[#e8e2d8]/90 bg-[#fffcf7]/80 px-1 shadow-[0_1px_0_rgba(28,25,23,0.04)] sm:px-2">
              {ESSENTIAL_FAQ.map((item) => (
                <AccordionItem key={item.id} value={item.id} className={accordionItemClass}>
                  <AccordionTrigger className={triggerClass}>
                    <span className="pr-6">{item.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className={contentClass}>{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* More topics */}
        <section id="dna" className="scroll-mt-24 border-t border-[#e8e2d8]/80 bg-[#f5f2eb]/50 px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-2xl">
            <h2 className="mb-2 font-serif text-xl font-normal text-[#1c1917] sm:text-2xl">More topics</h2>
            <p className="mb-10 text-sm leading-relaxed text-[#5c534a]">
              A little extra detail on DNA, sharing, and keeping your archive.
            </p>
            <div className="space-y-12">
              {MORE_FAQ.map((block) => (
                <div key={block.title}>
                  <h3 className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#8b7355]">
                    {block.title}
                  </h3>
                  <Accordion type="single" collapsible className="divide-y divide-[#e8e2d8] rounded-xl border border-[#e8e2d8]/80 bg-[#faf8f5]/90 px-1 sm:px-2">
                    {block.items.map((item) => (
                      <AccordionItem key={item.id} value={item.id} className={accordionItemClass}>
                        <AccordionTrigger className={`${triggerClass} py-4 text-[0.98rem]`}>
                          <span className="pr-6">{item.q}</span>
                        </AccordionTrigger>
                        <AccordionContent className={`${contentClass} pb-5`}>{item.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="privacy" className="scroll-mt-24 border-t border-[#e8e2d8]/80 px-4 py-12 sm:px-6">
          <div className="mx-auto max-w-2xl">
            <h2 className="font-serif text-xl font-normal text-[#1c1917]">Privacy Policy</h2>
            <p className="mt-4 text-sm leading-relaxed text-[#5c534a]">
              Your archive is private by default. We do not sell your family information. You control invites, memorial
              visibility, and exports. For full policy details before launch, contact{' '}
              <a href="mailto:support@mylegacybridge.com" className="underline underline-offset-2">support@mylegacybridge.com</a>.
            </p>
          </div>
        </section>

        <section id="terms" className="scroll-mt-24 border-t border-[#e8e2d8]/80 px-4 py-12 sm:px-6">
          <div className="mx-auto max-w-2xl">
            <h2 className="font-serif text-xl font-normal text-[#1c1917]">Terms of use</h2>
            <p className="mt-4 text-sm leading-relaxed text-[#5c534a]">
              MyLegacyBridge is provided for personal family archiving. You retain ownership of content you upload.
              Abuse, impersonation, and unlawful use are not permitted. Full terms will be published before general launch.
            </p>
          </div>
        </section>

        <section id="data" className="scroll-mt-24 border-t border-[#e8e2d8]/80 px-4 py-12 sm:px-6">
          <div className="mx-auto max-w-2xl">
            <h2 className="font-serif text-xl font-normal text-[#1c1917]">Data deletion &amp; privacy requests</h2>
            <p className="mt-4 text-sm leading-relaxed text-[#5c534a]">
              You can delete individual content from your archive or request full account deletion. Email{' '}
              <a href="mailto:support@mylegacybridge.com" className="underline underline-offset-2">support@mylegacybridge.com</a>{' '}
              with the address on your account and we will confirm next steps.
            </p>
          </div>
        </section>

        <section id="memorial" className="scroll-mt-24 border-t border-[#e8e2d8]/80 px-4 py-12 sm:px-6">
          <div className="mx-auto max-w-2xl">
            <h2 className="font-serif text-xl font-normal text-[#1c1917]">Memorial guidance</h2>
            <p className="mt-4 text-sm leading-relaxed text-[#5c534a]">
              Memorial pages are optional and private until you publish. Tributes can be moderated by the family.
              QR keepsakes link to the live page — update content without reprinting when using dynamic links.
              Only publish when your family is ready.
            </p>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="border-t border-[#e8e2d8]/80 px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-xl text-center">
            <p className="text-base text-[#5c534a]">Still wondering where to begin?</p>
            <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:justify-center">
              <Link to="/register">
                <Button
                  className="h-12 w-full rounded-full bg-[#1c1917] px-8 text-[#faf7f2] hover:bg-[#2c2824] sm:w-auto"
                  type="button"
                >
                  Create an account
                  <ArrowRight className="ml-2 h-4 w-4 opacity-90" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="h-12 w-full rounded-full border-[#c9a961]/40 bg-transparent text-[#1c1917] hover:bg-[#faf8f5] sm:w-auto">
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <PublicSiteFooter />
    </div>
  );
};

export default FAQ;
